import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Modal, Form, Input, DatePicker, Select, InputNumber,
    Row, Col, Typography, message, Spin, Popconfirm, Tag, Tooltip, Space, Badge, Upload, Descriptions
} from 'antd';
import {
    PlusOutlined, FileTextOutlined, UploadOutlined, DeleteOutlined,
    EditOutlined, ArrowLeftOutlined, DownloadOutlined
} from '@ant-design/icons';
import {
    getLpo, createLpoTransaction, updateLpoTransaction,
    deleteLpoTransaction, downloadLpoStatement, imagepath
} from '../../api';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

export default function LpoLedger() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lpo, setLpo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        const res = await getLpo(id);
        if (res.success) {
            setLpo(res.data);
        } else {
            message.error(res.message);
        }
        setLoading(false);
    };

    const handleAddTransaction = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({ date: dayjs(), type: 'debit', amount: 0 });
        setModalVisible(true);
    };

    const handleEditTransaction = (record) => {
        setEditingId(record.id);
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(record.date),
            description: record.description,
            type: record.type,
            amount: record.amount,
        });
        setModalVisible(true);
    };

    const handleDeleteTransaction = async (txnId) => {
        const res = await deleteLpoTransaction(txnId);
        if (res.success) {
            message.success('Transaction deleted');
            fetchData();
        } else {
            message.error(res.message);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();

            Object.entries(values).forEach(([key, value]) => {
                if (key === 'date' && value) {
                    formData.append(key, value.format('YYYY-MM-DD'));
                } else if (key === 'attachment') {
                    if (value && value[0] && value[0].originFileObj) {
                        formData.append(key, value[0].originFileObj);
                    }
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            });

            let res;
            if (editingId) {
                res = await updateLpoTransaction(editingId, formData);
            } else {
                res = await createLpoTransaction(id, formData);
            }

            if (res.success) {
                message.success(editingId ? 'Transaction updated' : 'Transaction added');
                setModalVisible(false);
                fetchData();
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDownloadStatement = async () => {
        try {
            message.loading({ content: 'Generating PDF...', key: 'download_lpo' });
            await downloadLpoStatement(id);
            message.success({ content: 'Statement downloaded', key: 'download_lpo' });
        } catch (err) {
            message.error({ content: 'Download failed', key: 'download_lpo' });
        }
    };

    if (loading && !lpo) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
    if (!lpo) return <div style={{ padding: 24 }}><Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/lpos')}>Back to LPOs</Button></div>;

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (d) => dayjs(d).format('DD-MM-YYYY'),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (t) => (
                <Tag color={t === 'credit' ? 'green' : 'red'}>
                    {t.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Credit (AED)',
            key: 'credit',
            align: 'right',
            width: 150,
            render: (_, r) => r.type === 'credit' ? <Text strong style={{ color: '#52c41a' }}>{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> : '-',
        },
        {
            title: 'Debit (AED)',
            key: 'debit',
            align: 'right',
            width: 150,
            render: (_, r) => r.type === 'debit' ? <Text strong style={{ color: '#ff4d4f' }}>{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> : '-',
        },
        {
            title: 'Balance (AED)',
            dataIndex: 'balance_after',
            key: 'balance_after',
            align: 'right',
            width: 150,
            render: (b) => <Text strong>{Number(b).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>,
        },
        {
            title: 'File',
            dataIndex: 'attachment',
            key: 'attachment',
            width: 80,
            align: 'center',
            render: (a) => a ? (
                <a href={`${imagepath}${a}`} target="_blank" rel="noreferrer">
                    <DownloadOutlined style={{ fontSize: 18 }} />
                </a>
            ) : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditTransaction(record)}
                    />
                    <Popconfirm title="Delete entry?" onConfirm={() => handleDeleteTransaction(record.id)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/lpos')} style={{ marginBottom: 16 }}>
                    Back to LPO List
                </Button>

                <Card title="LPO Details" bordered={false} className="shadow-sm">
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="LPO Number"><Text strong>{lpo.lpo_number}</Text></Descriptions.Item>
                        <Descriptions.Item label="Client">{lpo.client?.name}</Descriptions.Item>
                        <Descriptions.Item label="Title">{lpo.title || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="LPO Date">{dayjs(lpo.date).format('DD MMMM YYYY')}</Descriptions.Item>
                        <Descriptions.Item label="Total LPO Amount">
                            <Text strong style={{ fontSize: 18 }}>AED {Number(lpo.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Current Balance">
                            <Text strong style={{ fontSize: 18, color: lpo.balance > 0 ? '#52c41a' : '#ff4d4f' }}>
                                AED {Number(lpo.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </Text>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4}>Transaction History / Ledger</Title>
                <Space>
                    <Button
                        icon={<FileTextOutlined />}
                        onClick={handleDownloadStatement}
                    >
                        Download Statement PDF
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTransaction}>
                        Add Debit / Credit Entry
                    </Button>
                </Space>
            </div>

            <Card bordered={false} className="shadow-sm">
                <Table
                    dataSource={lpo.transactions || []}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    bordered
                />
            </Card>

            <Modal
                title={editingId ? "Edit Transaction" : "Add Transaction Entry"}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                width={500}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="credit">Credit (Increase Balance)</Option>
                                    <Option value="debit">Debit (Decrease Balance)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Payment received, Service provided, Expense" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="amount" label="Amount (AED)" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="attachment"
                                label="Supporting Document"
                                valuePropName="fileList"
                                getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
                            >
                                <Upload beforeUpload={() => false} maxCount={1}>
                                    <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select File</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
}
